import { GlassCard, ScrollReveal } from '@/components/design-system';
import { EmptyState } from '@/components/poof-ui';
import { Badge } from '@/components/ui/badge';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyListings, type ListingsResponse } from '@/lib/collections/listings';
import { PLASTIC_TYPE_LABELS, formatCentsAsUsd, isPlasticType } from '@/utils/plastic-rates';
import { Package, PackageSearch } from 'lucide-react';

const STATUS_STYLES: Record<string, string> = {
  open: 'border-primary/40 bg-primary/10 text-primary',
  accepted: 'border-accent/40 bg-accent/10 text-accent',
  scheduled: 'border-[hsl(var(--amber))]/40 bg-[hsl(var(--amber))]/10 text-[hsl(var(--amber))]',
  completed: 'border-border/60 bg-secondary/40 text-muted-foreground',
};

interface MyListingsListProps {
  userAddress: string;
}

/** The household's own listings, realtime, newest first. */
export function MyListingsList({ userAddress }: MyListingsListProps) {
  const { data: listings, loading } = useRealtimeData<ListingsResponse[]>(
    subscribeManyListings,
    !!userAddress,
    `created by ${userAddress}, newest first`
  );

  const mine = (listings ?? []).filter((l) => l.createdBy === userAddress);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Package className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-heading text-lg font-bold text-foreground">My Listings</h2>
      </div>

      {!loading && mine.length === 0 && (
        <EmptyState
          icon={<PackageSearch />}
          title="No listings yet"
          description="Scan and publish your first recovery listing above."
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {mine.map((listing, i) => (
          <ScrollReveal key={listing.id} delay={Math.min(i * 0.05, 0.3)}>
            <GlassCard padding="sm" className="h-full">
              <div className="flex gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary/50">
                  {listing.photoUrl ? (
                    <img src={listing.photoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {isPlasticType(listing.plasticType)
                        ? PLASTIC_TYPE_LABELS[listing.plasticType]
                        : listing.plasticType}
                    </span>
                    <Badge
                      variant="outline"
                      className={STATUS_STYLES[listing.status] ?? STATUS_STYLES.open}
                    >
                      {listing.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {listing.locationName}, {listing.city}
                  </p>
                  <div className="mt-2 flex items-baseline gap-3">
                    <span className="text-sm font-medium text-foreground">{listing.estimatedKg} kg</span>
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-sm font-bold text-transparent">
                      {formatCentsAsUsd(listing.estimatedValue)}
                    </span>
                  </div>
                </div>
              </div>
            </GlassCard>
          </ScrollReveal>
        ))}
      </div>
    </div>
  );
}

export default MyListingsList;
